/**
 * Availability: the month grid and the precise one-request check. The engine (API-engines builder)
 * produces these; the guest app and, later, the concierge read the same shapes so the screen and the
 * words never disagree.
 */
import { z } from 'zod';
import { DateString, Id, InstantRange, TimeHHmm } from './common.js';
/** Most alternatives returned for one request. */
export const MAX_ALTERNATIVES = 6;
/** How far either side of the requested date nearby-date alternatives may come from. */
export const NEARBY_DATE_RADIUS_DAYS = 14;
/**
 * Per product per date:
 * - `open` — the request (or, in the grid, some window that day) is available;
 * - `held` — another lead's active hold overlaps a leaf room ("being considered");
 * - `booked` — a booking overlaps a leaf room;
 * - `closed` — see `ClosureReason`;
 * - `limited` — the requested window is unavailable but another window that day is.
 */
export const DayStatus = z.enum(['open', 'held', 'booked', 'closed', 'limited']);
export const ClosureReason = z.enum([
    'weekly',
    'shabbat',
    'holiday',
    'blackout',
    'outside_hours',
    'inactive',
    'not_yet_open',
]);
/** `GET /g/availability?from=&to=&product=&guests=` */
export const AvailabilityQuery = z.object({
    from: DateString,
    to: DateString,
    product: Id.optional(),
    guests: z.coerce.number().int().min(1).max(2000).optional(),
});
export const ProductDayStatus = z.object({
    productId: Id,
    status: DayStatus,
    /** Set when `status` is `closed`. */
    closureReason: ClosureReason.nullable(),
});
export const AvailabilityDay = z.object({
    date: DateString,
    products: z.array(ProductDayStatus),
});
export const AvailabilityGrid = z.object({
    from: DateString,
    to: DateString,
    /** The venue timezone every date and time is in (`America/New_York`). */
    timezone: z.string(),
    days: z.array(AvailabilityDay),
});
/** `GET /g/availability/day?date=&product=&guests=&start=&duration=` (duration in minutes) */
export const DayCheckQuery = z.object({
    date: DateString,
    product: Id,
    guests: z.coerce.number().int().min(1).max(2000).optional(),
    start: TimeHHmm,
    duration: z.coerce.number().int().min(30).max(1440),
});
/**
 * Ordered, most date-preserving first: same date another product that fits the headcount → same
 * product another window the same day → nearby dates (±14 days, nearest first).
 */
export const AlternativeKind = z.enum([
    'same_date_other_product',
    'same_product_other_window',
    'nearby_date',
]);
export const Alternative = z.object({
    kind: AlternativeKind,
    productId: Id,
    date: DateString,
    start: TimeHHmm,
    durationMinutes: z.number().int().positive(),
});
export const DayCheck = z.object({
    date: DateString,
    productId: Id,
    start: TimeHHmm,
    durationMinutes: z.number().int().positive(),
    guests: z.number().int().nullable(),
    status: DayStatus,
    closureReason: ClosureReason.nullable(),
    /** The occupied window: `[start − setup, start + duration + teardown)`. */
    window: InstantRange,
    alternatives: z.array(Alternative).max(MAX_ALTERNATIVES),
});
//# sourceMappingURL=availability.js.map